<script setup lang="ts">
import type { DropdownMenuItem } from '#ui/types';

// COMPOSABLES
const { isLoggedIn, data, logout } = useAuthUtils();
const { tt } = useTypedI18n();
const accountMenuItems = useAccountMenuItems();
const { trackOpenAccountMenu, trackLogout } = useTracking();

// DATA
const isDropdownOpen = ref(false);
const items = computed<DropdownMenuItem[][]>(() => [
    ...insertIf(!!data.value?.user?.email, [
        {
            slot: 'account' as const,
            disabled: true,
        },
    ]),
    ...accountMenuItems.value.map(accountItemGroup =>
        accountItemGroup.map(({ label, to, onSelect }) => ({
            label,
            onClick: (e: Event): void => {
                onSelect?.(e);
                navigateTo(to);
            },
        }))
    ),
    [
        {
            label: tt('common.logout'),
            slot: 'logout' as const,
            onClick: (): void => {
                trackLogout();
                logout();
            },
        },
    ],
]);

const ui = {
    item: 'py-2 data-disabled:cursor-cursor-text data-disabled:opacity-100 data-disabled:select-text truncate font-medium',
    content: 'w-96 rounded-xl p-2',
};

// METHODS
const onDropdownMenuUpdateOpen = (open: boolean): void => {
    open && trackOpenAccountMenu();
};

// WATCHERS
watchEffect(() => {
    if (!isLoggedIn.value) isDropdownOpen.value = false;
});
</script>

<template>
    <UDropdownMenu
        v-model:open="isDropdownOpen"
        :items="items"
        :ui="ui"
        :modal="false"
        :content="{
            align: 'end',
            side: 'bottom',
        }"
        @update:open="onDropdownMenuUpdateOpen"
    >
        <UButton
            color="neutral"
            variant="outline"
            :avatar="{
                icon: 'lucide:user',
                class: 'size-11 bg-transparent',
                size: '3xl',
                ui: { icon: 'text-highlighted' },
            }"
            size="xl"
            class="rounded-full px-0 py-0"
        />

        <template #account-label>
            <div class="min-w-0 text-left font-bold">
                <p>{{ tt('common.yourAccount') }}:</p>
                <p class="text-primary truncate">
                    {{ data?.user?.email }}
                </p>
            </div>
        </template>

        <template #item-trailing>
            <UIcon name="lucide:chevron-right" class="ms-auto shrink-0 text-2xl" />
        </template>

        <template #logout-trailing>
            <UIcon name="lucide:log-out" class="ms-auto shrink-0 text-xl" />
        </template>
    </UDropdownMenu>
</template>
